import React, { useEffect, useRef, useState } from 'react';

import { faThumbsDown } from '@fortawesome/pro-regular-svg-icons/faThumbsDown';
import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons/faThumbsUp';

import { FEEDBACK_NEGATIVE, FEEDBACK_POSITIVE } from '@captivateiq/assistant/types/api';
import { QBox, QIconButton, QStack, QTooltip, QTypography } from '@captivateiq/quotient';

import { FeedbackTextForm } from '../TabsAIShell/ChatTab/ChatView/FeedbackTextForm';

export type FeedbackButtonsProps = {
  feedback: number | null;
  feedbackResponse?: string | null;
  onFeedback: (feedback: number) => void;
  onFeedbackResponse?: (text: string) => Promise<void>;
};

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  feedback,
  feedbackResponse,
  onFeedback,
  onFeedbackResponse,
}) => {
  const pendingFeedbackRef = useRef<number | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const effectiveFeedback = feedback ?? pendingFeedbackRef.current;
  const showTextForm = effectiveFeedback != null && !feedbackResponse;

  useEffect(() => {
    if (feedback != null) {
      pendingFeedbackRef.current = null;
    }
  }, [feedback]);

  const handleFeedbackClick = (value: number) => {
    pendingFeedbackRef.current = feedback === value ? null : value;
    onFeedback(value);
  };

  return (
    <QBox sx={{ pt: 1, pb: 3 }}>
      <QStack direction="row" spacing="4px">
        <QTooltip placement="top" title="Helpful">
          <QIconButton
            aria-label="Helpful"
            data-testid="feedback-thumbs-up"
            edge="start"
            icon={faThumbsUp}
            size="small"
            sx={{
              color: feedback === FEEDBACK_POSITIVE ? 'brand.primary.700' : 'neutral.primary.400',
              '&:hover': { color: 'brand.primary.700' },
            }}
            onClick={() => handleFeedbackClick(FEEDBACK_POSITIVE)}
          />
        </QTooltip>
        <QTooltip placement="top" title="Not helpful">
          <QIconButton
            aria-label="Not helpful"
            data-testid="feedback-thumbs-down"
            icon={faThumbsDown}
            size="small"
            sx={{
              color: feedback === FEEDBACK_NEGATIVE ? 'denotive.danger.700' : 'neutral.primary.400',
              '&:hover': { color: 'denotive.danger.700' },
            }}
            onClick={() => handleFeedbackClick(FEEDBACK_NEGATIVE)}
          />
        </QTooltip>
      </QStack>
      {onFeedbackResponse && showTextForm && (
        <FeedbackTextForm
          existingResponse={feedbackResponse}
          onSubmit={async (_, text) => {
            await onFeedbackResponse(text);
            setJustSubmitted(true);
            setTimeout(() => setJustSubmitted(false), 3000);
          }}
        />
      )}
      {justSubmitted && (
        <QTypography color="text.onSurface.background.mediumContrast" sx={{ mt: 0.5 }} variant="smallRegular">
          Thanks for your feedback!
        </QTypography>
      )}
      {feedbackResponse && !justSubmitted && (
        <QTypography color="text.onSurface.background.mediumContrast" sx={{ mt: 0.5 }} variant="smallRegular">
          You left feedback
        </QTypography>
      )}
    </QBox>
  );
};
